<script setup>
/**
 * SzContactCard — card de contato no estilo Figma Brasa.
 *
 * Layout (~451×96 ou 451×116 com etiquetas):
 *   [Avatar 56x56]  Nome - ID                       (linha 1: nome bold)
 *                   +55 (xx) 98xxx-xxxx              (linha 2: telefone)
 *                   [icone] Inbox                    (linha 3: inbox c/ ícone do canal)
 *                   [● etiqueta1] [● etiqueta2] +N   (linha 4 opcional: etiquetas)
 */
import { computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useStore } from 'vuex';
import CardLabels from 'dashboard/components-next/Conversation/ConversationCard/CardLabels.vue';

const props = defineProps({
  contact: { type: Object, required: true },
  isSelected: { type: Boolean, default: false },
  isActive: { type: Boolean, default: false },
});
const emit = defineEmits(['toggle', 'select']);

const router = useRouter();
const route = useRoute();
const store = useStore();

const id = computed(() => props.contact.id);
const name = computed(() => props.contact.name || '');
const phoneNumber = computed(() => props.contact.phone_number || props.contact.phoneNumber || '');
const thumbnail = computed(() => props.contact.thumbnail || '');

// Inbox principal (primeiro vínculo)
const primaryInbox = computed(() => {
  const inboxes = props.contact.contact_inboxes || props.contact.contactInboxes || [];
  if (!inboxes.length) return null;
  return inboxes[0]?.inbox || inboxes[0];
});
const inboxName = computed(() => primaryInbox.value?.name || '');
const inboxChannel = computed(() => {
  const ch = primaryInbox.value?.channel_type || primaryInbox.value?.channelType || '';
  return ch.toLowerCase();
});

// Etiquetas — reaproveita CardLabels da Conversation:
// - labelTitles: array de strings (títulos das labels do contato)
// - accountLabels: array completo {title, color} da conta (vem do Vuex)
const labels = computed(() => props.contact.labels || []);
const labelTitles = computed(() =>
  labels.value.map(l => (typeof l === 'string' ? l : l?.title)).filter(Boolean)
);
const accountLabels = computed(() => store.getters['labels/getLabels'] || []);
const hasLabels = computed(() => labelTitles.value.length > 0);

// Iniciais pra avatar fallback
const initials = computed(() => {
  if (!name.value) return '';
  return name.value
    .split(' ')
    .map(w => w.charAt(0))
    .join('')
    .substring(0, 2)
    .toUpperCase();
});

function onClick() {
  // Navega pra rota do contato
  const routeName = route.name === 'contacts_dashboard_segments_index'
    ? 'contacts_edit_segment'
    : (route.name === 'contacts_dashboard_labels_index' ? 'contacts_edit_label' : 'contacts_edit');
  router.push({
    name: routeName,
    params: {
      contactId: id.value,
      ...(route.params.segmentId && { segmentId: route.params.segmentId }),
      ...(route.params.label && { label: route.params.label }),
    },
    query: route.query,
  });
}

</script>

<template>
  <div
    class="sz-contact-card"
    :class="{ 'sz-contact-card--active': isActive }"
    role="button"
    tabindex="0"
    @click="onClick"
    @keydown.enter.prevent="onClick"
  >
    <!-- Linha topo: avatar + body (nome/telefone) -->
    <div class="sz-cc-top">
      <!-- Avatar 56x56 -->
      <div class="sz-cc-avatar">
        <img v-if="thumbnail" :src="thumbnail" :alt="name" class="sz-cc-avatar-img" />
        <span v-else-if="initials" class="sz-cc-avatar-initials">{{ initials }}</span>
        <svg v-else width="32" height="32" viewBox="0 0 24 24" fill="#A1A1A1" aria-hidden="true">
          <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
        </svg>
      </div>

      <!-- Body (só nome + telefone) -->
      <div class="sz-cc-body">
        <div class="sz-cc-name">{{ name }}</div>
        <div v-if="phoneNumber" class="sz-cc-phone">{{ phoneNumber }}</div>
      </div>
    </div>

    <!-- Linha etiquetas — fora do body, ocupa largura total do card -->
    <div v-if="hasLabels" class="sz-cc-labels-wrap">
      <CardLabels
        :conversation-labels="labelTitles"
        :account-labels="accountLabels"
      />
    </div>
  </div>
</template>

<style scoped>
.sz-contact-card {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px 16px;
  background: #FFFFFF;
  border-bottom: 1px solid #E5E7EB;
  cursor: pointer;
  transition: background 0.12s;
  font-family: 'Inter', system-ui, sans-serif;
  box-sizing: border-box;
}
.sz-cc-top {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  min-width: 0;
}
.sz-contact-card:hover { background: #F9FAFB; }
.sz-contact-card--active { background: #EEF6F0; }

/* Dark mode */
.dark .sz-contact-card {
  background: #1D1E21;
  border-bottom-color: #242528;
}
.dark .sz-contact-card:hover { background: #242528; }
.dark .sz-contact-card--active { background: #2D2E33; }

/* Avatar 56x56 */
.sz-cc-avatar {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #F5F5F5;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
}
.sz-cc-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.sz-cc-avatar-initials {
  font-size: 18px;
  font-weight: 600;
  color: #525252;
}
.dark .sz-cc-avatar { background: #242528; }
.dark .sz-cc-avatar-initials { color: #E5E7EB; }

/* Body */
.sz-cc-body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.sz-cc-name {
  font-size: 14px;
  font-weight: 600;
  color: #000000;
  line-height: 20px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.dark .sz-cc-name { color: #FFFFFF; }

.sz-cc-phone {
  font-size: 12px;
  font-weight: 400;
  color: #525252;
  line-height: 16px;
}
.dark .sz-cc-phone { color: #9797A0; }

.sz-cc-inbox {
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  min-width: 0;
  font-size: 12px;
  font-weight: 400;
  color: #525252;
  line-height: 16px;
}
.sz-cc-inbox > span {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.sz-cc-inbox svg { flex-shrink: 0; }
.dark .sz-cc-inbox { color: #9797A0; }
.dark .sz-cc-inbox svg { stroke: #9797A0; }

/* Wrapper das labels — dá largura definida pro CardLabels calcular overflow */
.sz-cc-labels-wrap {
  width: 100%;
  min-width: 0;
  margin-top: 2px;
  align-self: stretch;
}
.dark .sz-cc-label-more {
  background: rgba(56, 142, 60, 0.22) !important;
  color: #4ADE80 !important;
}
</style>
